/* ===========================================================
   Mobile bottom navigation behavior — Tinker theme bindings
   - Tap bounce animation
   - Live cart count from Shopify cart events + initial fetch
   - Search → clicks <search-button> inner button (mirrors header)
   - Cart   → clicks cart-drawer-component trigger (mirrors header)
   - Profile → reaches into <shopify-account> shadow DOM and clicks
              the inner button (the only thing that actually opens
              Shopify's customer-account popup)
   No custom UI is created. We only re-trigger the theme's own elements.
   =========================================================== */

(function () {
  'use strict';

  const nav = document.querySelector('[data-mobile-bottom-nav]');
  if (!nav) return;

  /* ============ Helpers ============ */

  // Robust click for buttons with attribute-based directives (Tinker uses
  // on:click="..." — these are wired via document-level click delegation,
  // so a normal .click() works).
  const tryClick = (el) => {
    if (!el) return false;
    try {
      el.click();
      return true;
    } catch (_) {
      try {
        el.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            composed: true,
            view: window,
          })
        );
        return true;
      } catch (__) {
        return false;
      }
    }
  };

  // Composed event so it crosses shadow DOM boundaries.
  const dispatchComposedClick = (el) => {
    if (!el) return false;
    try {
      el.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          composed: true,
          view: window,
        })
      );
      return true;
    } catch (_) {
      return false;
    }
  };

  const isModified = (e) =>
    e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;

  /* ============ Tap bounce animation ============ */
  const links = nav.querySelectorAll('.mbn__link');
  const TAP_RESET_MS = 220;

  const onTapStart = (e) => e.currentTarget.classList.add('is-tapped');
  const onTapEnd = (e) => {
    const el = e.currentTarget;
    setTimeout(() => el.classList.remove('is-tapped'), TAP_RESET_MS);
  };

  links.forEach((link) => {
    link.addEventListener('touchstart', onTapStart, { passive: true });
    link.addEventListener('touchend', onTapEnd, { passive: true });
    link.addEventListener('touchcancel', onTapEnd, { passive: true });
    link.addEventListener('mousedown', onTapStart);
    link.addEventListener('mouseup', onTapEnd);
    link.addEventListener('mouseleave', onTapEnd);
  });

  /* ============ Cart count badge ============ */
  const badge = nav.querySelector('[data-cart-count]');

  const updateBadge = (count) => {
    if (!badge) return;
    const n = Number(count) || 0;
    badge.textContent = n;
    if (n > 0) {
      badge.classList.remove('is-empty');
      badge.setAttribute('aria-hidden', 'false');
    } else {
      badge.classList.add('is-empty');
      badge.setAttribute('aria-hidden', 'true');
    }
  };

  document.addEventListener('cart:updated', (e) => {
    const count =
      e?.detail?.cart?.item_count ??
      e?.detail?.item_count ??
      null;
    if (count !== null) updateBadge(count);
  });

  document.addEventListener('cart:refresh', () => {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => updateBadge(cart.item_count))
      .catch(() => {});
  });

  /* ============ Search → click <search-button> inner button ============
     Tinker structure:
     <search-button class="search-action ...">
       <button on:click="#search-modal/showDialog" aria-label="Search" ...>
     Clicking that <button> triggers Tinker's framework directive which
     opens the search modal. Same path as a real tap.
  ============================================================== */
  const triggerSearch = () => {
    // 1. Click the inner button of search-button (most reliable)
    const candidates = [
      'search-button button[aria-label="Search"]',
      'search-button button',
      'button[aria-haspopup="dialog"][aria-label="Search"]',
      'button[aria-label="Search"]',
      '.search-action button',
    ];
    for (const sel of candidates) {
      const els = document.querySelectorAll(sel);
      for (const el of els) {
        if (!nav.contains(el)) {
          if (tryClick(el)) return true;
        }
      }
    }

    // 2. Last-resort fallback: directly call method on #search-modal if it exists
    const modal =
      document.querySelector('#search-modal') ||
      document.querySelector('search-modal');
    if (modal) {
      for (const method of ['showDialog', 'show', 'open', 'showModal']) {
        if (typeof modal[method] === 'function') {
          try {
            modal[method]();
            return true;
          } catch (_) {}
        }
      }
    }

    return false;
  };

  const searchLink = nav.querySelector('[data-mbn-search]');
  if (searchLink) {
    searchLink.addEventListener('click', (e) => {
      if (isModified(e)) return;
      if (triggerSearch()) {
        e.preventDefault();
      }
    });
  }

  /* ============ Cart → open cart-drawer-component ============ */
  const triggerCart = () => {
    // 1. Click the cart drawer trigger button
    const candidates = [
      '[data-testid="cart-drawer-trigger"]',
      'cart-drawer-component button[aria-label="Cart"]',
      'cart-drawer-component button[aria-haspopup="dialog"]',
    ];
    for (const sel of candidates) {
      const els = document.querySelectorAll(sel);
      for (const el of els) {
        if (!nav.contains(el)) {
          if (tryClick(el)) return true;
        }
      }
    }

    // 2. Direct method call on cart-drawer-component
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
        return true;
      } catch (_) {}
    }

    return false;
  };

  const cartLink = nav.querySelector('[data-mbn-cart]');
  if (cartLink) {
    cartLink.addEventListener('click', (e) => {
      if (isModified(e)) return;
      if (triggerCart()) {
        e.preventDefault();
      }
    });
  }

  // Sync cart badge once on first interaction
  let polled = false;
  cartLink?.addEventListener(
    'pointerenter',
    () => {
      if (polled) return;
      polled = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => updateBadge(cart.item_count))
        .catch(() => {});
    },
    { once: true, passive: true }
  );

  /* ============ Profile → click <shopify-account> internal button ============
     <shopify-account> is Shopify's web component with an OPEN shadow root.
     The actual interactive button lives INSIDE the shadow DOM. A normal
     .click() on the host doesn't always reach it. We:
       1. Reach into shadowRoot, find the inner button, click it (most reliable)
       2. Fall back to dispatching a composed click on the slot content
       3. Last resort: click the host or .account-button
  ============================================================== */
  const triggerAccount = () => {
    const sa = document.querySelector('shopify-account');
    if (!sa || nav.contains(sa)) {
      // Fallback: click .account-button
      const accountBtn = document.querySelector('.account-button');
      if (accountBtn && !nav.contains(accountBtn)) {
        return tryClick(accountBtn);
      }
      return false;
    }

    // 1. Reach into the open shadow root and click the actual interactive element
    if (sa.shadowRoot) {
      const inner =
        sa.shadowRoot.querySelector('button[aria-haspopup]') ||
        sa.shadowRoot.querySelector('button') ||
        sa.shadowRoot.querySelector('a') ||
        sa.shadowRoot.querySelector('[role="button"]');
      if (inner) {
        if (tryClick(inner)) return true;
      }
    }

    // 2. Dispatch a composed click on slot content (light DOM) — bubbles through shadow
    const slot =
      sa.querySelector('[slot="signed-out-avatar"]:not([class*="hidden"])') ||
      sa.querySelector('[slot="signed-in-avatar"]') ||
      sa.querySelector('[slot="signed-out-avatar"]') ||
      sa.querySelector('.account-button__fallback') ||
      sa.querySelector('span, button, a, svg');
    if (slot && dispatchComposedClick(slot)) {
      return true;
    }

    // 3. Click the host element directly
    if (tryClick(sa)) return true;

    // 4. Click the wrapping .account-button div
    const accountBtn = sa.closest('.account-button') || document.querySelector('.account-button');
    if (accountBtn && !nav.contains(accountBtn)) {
      return tryClick(accountBtn);
    }

    return false;
  };

  const profileLink = nav.querySelector('[data-mbn-profile]');
  if (profileLink) {
    profileLink.addEventListener('click', (e) => {
      if (isModified(e)) return;
      if (triggerAccount()) {
        e.preventDefault();
      }
    });
  }
})();
